<template>
  <!-- https://sortablejs.github.io/Vue.Draggable/ -->
  <div
    v-if="showColumnsSetting"
    ref="setRef"
    class="table-columns-setting"
    :style="{ top: `${pagePosition}px` }"
  >
    <div
      class="max-h-[310px] overflow-y-auto"
    >
      <ul class="el-dropdown-menu outline-none">
        <draggable
          :list="columns"
          item-key="property"
          :disabled="!store.enabledDrag"
          @end="onDragEnd"
        >
          <template #item="{ element }">
            <li
              :class="`el-dropdown-menu__item flex justify-between items-center ${element.property === 'actions' ? 'hidden' : ''}`"
              @click="toggleColumnChecked(element)"
            >
              <span class="flex items-center">
                <el-checkbox
                  :model-value="visibleColumns.includes(element.property)"
                  :value="element.property"
                />
                <span
                  :title="element.label"
                  :data-propname="element.property"
                  :data-checked="visibleColumns.includes(element.property)"
                  class="inline-block max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
                >
                  {{ element.label }}
                </span>
              </span>

              <svg-icon
                v-if="store.enabledDrag"
                title="拖拽排序" icon="iconoir:sort"
                class="ml-3 !w-[18px] !h-[18px]"
              />
            </li>
          </template>
        </draggable>
      </ul>
    </div>

    <span
      class="el-popper__arrow"
      style="position: absolute;right: 20px;top: -6px;z-index: 3;"
    />
  </div>
</template>

<script setup lang="ts">
import { SvgIcon } from '@vue3/components';
import draggable from 'vuedraggable';
import { onBeforeMount, onMounted, ref, toRaw } from 'vue';
import { store } from './tableColsSetStore';

const pagePosition = ref(0);
const showColumnsSetting = ref(false);

const setRef = ref<any>(null);
const columns = ref<Array<{ property: string; label: string }>>([]);
const visibleColumns = ref<string[]>([]);
const originColumnKeys = ref<string[]>([]);

store.hideColumnsSetting = () => {
  showColumnsSetting.value = false;
};

store.initColumns = () => {
  if (!store.tableContainer) return;

  initOriginVisibleColumns();
  appendSettingDomToActionColumn();
};

function onDragEnd() {
  const d = setRef.value;
  const allLi = d.querySelectorAll('li .inline-block');

  const allKeys = [] as string[];
  const visibleKeys = [] as string[];

  for (const v of allLi) {
    const li = v as HTMLElement;
    const checked = li.dataset.checked === 'true';

    if (checked) {
      visibleKeys.push(li.dataset.propname);
    }

    allKeys.push(li.dataset.propname);
  }

  originColumnKeys.value = allKeys;
  visibleColumns.value = visibleKeys;
  store.updateVisibleColumns(visibleKeys);
}

function toggleColumnChecked(column: { property: string; label: string }) {
  let changedKeyNames = toRaw(visibleColumns.value);

  const checked = visibleColumns.value.includes(column.property);

  if (checked) {
    changedKeyNames = changedKeyNames.filter((v) => v !== column.property);
  } else {
    changedKeyNames.push(column.property);
  }

  changedKeyNames = originColumnKeys.value.filter((item) => changedKeyNames.includes(item));

  visibleColumns.value = changedKeyNames;
  store.updateVisibleColumns(changedKeyNames);
}

function toggleCheckNodesWrapper(e: MouseEvent) {
  const setIcon = e.currentTarget as HTMLElement;

  if (setIcon) {
    const setIconTop = setIcon.getBoundingClientRect().top;

    console.log(setIconTop);

    showColumnsSetting.value = !showColumnsSetting.value;
    pagePosition.value = setIconTop + 36;
  }
}

function initOriginVisibleColumns() {
  const theadThs = store.tableContainer?.querySelectorAll('.el-table__header th');

  columns.value = [];
  visibleColumns.value = [];
  originColumnKeys.value = [];

  for (const v of theadThs) {
    const th = v as HTMLElement;

    if (th.dataset.propname) {
      const item = {
        property: th.dataset.propname,
        label: th.innerText as string,
      };

      columns.value.push(item);
      originColumnKeys.value.push(item.property);
      visibleColumns.value.push(th.dataset.propname);
    }
  }
}

/**
 * 将列设置dom添加到操作列
 * @param tableContainer - 表格容器
 */
function appendSettingDomToActionColumn() {
  const actionColumnDom = getActionColumnDom();

  if (!actionColumnDom) return;

  const existSettingDom = actionColumnDom.querySelector('.sys-column-setting');

  if (existSettingDom) return;

  const settingDom = document.createElement('div');
  const cell = actionColumnDom.querySelector('.cell') as HTMLElement;

  settingDom.className = 'sys-column-setting';
  settingDom.innerHTML = '<span></span>';

  cell.style.display = 'flex';
  cell.style.justifyContent = 'center';
  cell.appendChild(settingDom);

  settingDom.addEventListener('click', toggleCheckNodesWrapper, false);
}

function getActionColumnDom() {
  if (!store.tableContainer) return null;

  const actionColumnDom = store.tableContainer.querySelector(
    `[data-propname="${store.operatorProperty}"]`,
  );

  return actionColumnDom;
}

function whiteSpaceClickHandler(e: MouseEvent) {
  const settingDom = store.tableContainer?.querySelector('.sys-column-setting') as HTMLElement;

  if (!settingDom || !setRef.value) return;

  if (setRef.value.contains(e.target as Node) || settingDom.contains(e.target as Node)) {
    return;
  }

  showColumnsSetting.value = false;
}

onMounted(() => {
  document.body.removeEventListener('click', whiteSpaceClickHandler, false);
  document.body.addEventListener('click', whiteSpaceClickHandler, false);
});

onBeforeMount(() => {
  document.body.removeEventListener('click', whiteSpaceClickHandler, false);
});
</script>

<style scoped lang="scss">
.table-columns-setting {
  @apply fixed z-50 rounded min-w-[188px] right-[76px] top-[368px];

  box-shadow: 0px 0px 12px rgba(0, 0, 0, .12);
  border: 1px solid #e4e7ed;

  :deep(.el-dropdown-menu__item) {
    @apply text-sm !px-4;
  }

  :deep(.el-popper__arrow:before) {
    background: #fff;
  }

  :deep(.inline-block) {
    @apply select-none;
  }
}
</style>
